import { Dispatch, SetStateAction, useMemo, useState } from 'react'
import { Sidebar } from 'primereact/sidebar'
import { useTranslation } from 'react-i18next'

// local imports
import DateUtil from 'utils/DateUtil'
import { IFilteredGymTrainerList, IGetGymTrainerTimeTableList, ISaveGymTrainerReservation } from 'types/GymType'
import useSaveGymTrainerReservation from '../../../hooks/use-save-trainer-reservation'
import {
  ContentArea,
  Header,
  ImagePreview,
  InfoList,
  InfoListItem,
  ReservationSummary,
  SidebarActionArea,
  SidebarCancelButton,
  SidebarConfirmButton,
  SidebarContainer,
} from '../../styled'

/* assets */
import trainerIcon from 'assets/icons/avatar.svg'
import calendarr from 'assets/icons/calendar-gray.svg'
import time from 'assets/icons/time.svg'
import location from 'assets/icons/location-marker-gray.svg'
import building from 'assets/icons/bina-gray.svg'
import resIcon from 'assets/icons/gym-res.svg'
import FavoriteIconButton from 'components/ui/FavoriteIconButton'
import GymService from 'service/GymService'
import AuthUtil from 'utils/AuthUtil'

interface ScheduleSidebarProps {
  openModal: () => void
  closeAfterSave: () => void
  visible: boolean
  setVisible: Dispatch<SetStateAction<boolean>>
  trainerInfo: IFilteredGymTrainerList
  appointmentInfo: IGetGymTrainerTimeTableList
}

export default function ScheduleSidebar({
  trainerInfo,
  appointmentInfo,
  visible,
  setVisible,
  openModal,
  closeAfterSave,
}: ScheduleSidebarProps) {
  const { t } = useTranslation()
  const date = DateUtil.dateFormat(new Date(appointmentInfo.reservationEndHour))
  const appointmentTime = `${appointmentInfo.reservationStartHourStr} - ${appointmentInfo.reservationEndHourStr}`
  const employee = AuthUtil.getEmployeeSummary()

  // Gym Trainer information desctructuring
  const {
    id: _Id,
    isFavorite,
    gymTrainer: { fullName, id },
    gymSaloon: {
      floor: { blockName, cityName, countryName, districtName, locationName, name },
    },
  } = trainerInfo || {}
  const salonLocationName = `${locationName}, ${districtName}, ${cityName}, ${countryName} `

  /* Handle Save Gym Salon personal work reservation | Custom Hook */
  const resInfo: ISaveGymTrainerReservation = {
    gymTrainerId: id,
    gymSaloonTrainerId: appointmentInfo.gymSaloonTrainerId,
    reservationStartTime: appointmentInfo.reservationStartHour,
    reservationEndTime: appointmentInfo.reservationEndHour,
  }
  const { loading, saveGymTrainerReservation } = useSaveGymTrainerReservation(resInfo, closeAfterSave)

  const closeSidebar = () => {
    setVisible(false)
    openModal()
  }

  const reservationInfoItems = useMemo(
    () => [
      {
        id: 'trainer-name',
        icon: trainerIcon,
        title: t('gym-res-modal.trainer-name'),
        value: fullName,
      },
      {
        id: 'location-info',
        icon: location,
        title: t('gym-res-modal.salon-location'),
        value: salonLocationName,
      },
      {
        id: 'block-floor-info',
        icon: building,
        title: t('gym-res-modal.block-floor'),
        value: `${blockName} / ${name}`,
      },
      {
        id: 'date-info',
        icon: calendarr,
        title: t('gym-res-modal.reservation-date'),
        value: date,
      },
      {
        id: 'time-info',
        icon: time,
        title: t('gym-res-modal.reservation-time'),
        value: appointmentTime,
      },
    ],
    [blockName, name, appointmentTime, date, salonLocationName, fullName, t]
  )

  /* Favorite Gym Trainer */
  const [favorite, setFavorite] = useState(isFavorite)
  const handleAddFavorite = () => {
    GymService.addGymTrainerFavorite(_Id).then((r) => setFavorite(true))
  }
  const handleRemoveFavorite = () => {
    GymService.removeGymTrainerFavorite(_Id).then((r) => setFavorite(false))
  }

  return (
    <Sidebar
      style={{ width: '500px' }}
      className="of-bordered-sidebar-box"
      visible={visible}
      position="right"
      onHide={closeSidebar}
      dismissable={false}
      closeOnEscape={false}>
      <SidebarContainer>
        <ContentArea>
          <Header className="d-flex align-items-center gap-2">
            {t('gym-res-modal.gym-reservation')}
            {AuthUtil.isAdmin(employee?.authList) && (
              <FavoriteIconButton onSave={handleAddFavorite} onRemove={handleRemoveFavorite} isFavorite={favorite} />
            )}
          </Header>
          <ReservationSummary>{t('gym-res-modal.reservation-summary')}</ReservationSummary>
          <ImagePreview url={resIcon} />
          <InfoList>
            <div className="reservation-subtitle">{t('gym-res-modal.reservation-info')}</div>
            {reservationInfoItems.map((info) => (
              <InfoListItem key={info.id}>
                <img src={info.icon} alt={info.id} />
                <span className="reservation-info-title"> {info.title} : </span>
                <span className="reservation-info-value">{info.value}</span>
              </InfoListItem>
            ))}
          </InfoList>
        </ContentArea>

        {/* Action Buttons */}
        <SidebarActionArea>
          <SidebarCancelButton
            id="backToGymTrainerAppointment"
            className="p-button-outlined"
            label={t('coiffeur-res-modal.back-to-modal')}
            onClick={closeSidebar}
          />

          <SidebarConfirmButton
            id="saveGymTrainerAppointment"
            loading={loading}
            label={t('buttons.confirm')}
            onClick={saveGymTrainerReservation}
          />
        </SidebarActionArea>
      </SidebarContainer>
    </Sidebar>
  )
}
